(() => {
  if (customElements.get('product-checkbox-upsell')) return;

  class ProductCheckboxUpsell extends HTMLElement {
    // Serialize this component's writes, including duplicate instances in quick buy/editor previews.
    static queue = Promise.resolve();

    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.input = this.querySelector('input');
      this.error = this.querySelector('.pcu__error');
      this.status = this.querySelector('.pcu__status');
      this.busy = false;
      const options = { signal: this.controller.signal };
      this.input.addEventListener('change', () => this.toggle(), options);
      document.addEventListener('cart:change', (event) => {
        if (!this.busy && event.detail?.cart?.items) {
          this.revision = (this.revision || 0) + 1;
          this.sync(event.detail.cart);
        }
      }, options);
      document.addEventListener('cart:refresh', () => this.reload(), options);
      window.addEventListener('pageshow', () => this.reload(), options);
      this.reload();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.revision = (this.revision || 0) + 1;
    }

    async request(endpoint, body) {
      const response = await fetch(`${window.Shopify.routes.root}cart${endpoint}.js`, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || this.dataset.error);
      return result;
    }

    lines(cart) {
      return cart.items.filter(item => String(item.variant_id) === this.dataset.variantId &&
        item.properties?._checkbox_upsell === this.dataset.source);
    }

    sync(cart) {
      this.input.checked = this.lines(cart).length > 0;
      this.input.disabled = this.busy || (!this.input.checked && this.dataset.available !== 'true');
    }

    async reload() {
      if (this.busy) return;
      const revision = this.revision = (this.revision || 0) + 1;
      try {
        const cart = await this.request('');
        if (this.isConnected && !this.busy && revision === this.revision) this.sync(cart);
      } catch {
        // A click always reads the authoritative cart again before making any change.
        if (this.isConnected && !this.busy && revision === this.revision) {
          this.input.disabled = !this.input.checked && this.dataset.available !== 'true';
        }
      }
    }

    async toggle() {
      if (this.busy) return;
      const selected = this.input.checked;
      this.busy = true;
      this.revision = (this.revision || 0) + 1;
      this.input.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      this.status.textContent = '';
      const operation = async () => {
        let cart;
        try {
          cart = await this.request('');
          const lines = this.lines(cart);
          if (selected && lines.length === 0) {
            await this.request('/add', { items: [{
              id: Number(this.dataset.variantId), quantity: 1,
              properties: { _checkbox_upsell: this.dataset.source }
            }] });
            cart = await this.request('');
          } else if (!selected && lines.length) {
            // Use current line keys, never a variant id that could remove an unrelated purchase.
            cart = await this.request('/update', {
              updates: Object.fromEntries(lines.map(item => [item.key, 0]))
            });
          }
          this.sync(cart);
          this.status.textContent = this.input.checked ? this.dataset.added : this.dataset.removed;
        } catch (error) {
          // A write can succeed even if its response is lost: reconcile before allowing another click.
          try { cart = await this.request(''); } catch { /* Retain last known state. */ }
          if (cart) this.sync(cart);
          else this.input.checked = !selected;
          this.error.textContent = error.message || this.dataset.error;
          this.error.hidden = false;
        } finally {
          this.busy = false;
          this.input.disabled = !this.input.checked && this.dataset.available !== 'true';
          this.removeAttribute('aria-busy');
          // Impact's documented UI-only refresh updates counts and drawer without opening it.
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      };
      const pending = ProductCheckboxUpsell.queue.then(operation, operation);
      ProductCheckboxUpsell.queue = pending.catch(() => {});
      await pending;
    }
  }

  customElements.define('product-checkbox-upsell', ProductCheckboxUpsell);
})();
